import React, { FC, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { CaretRightOutlined, PauseOutlined, AudioMutedOutlined } from '@ant-design/icons';
import { formatAudioDuration } from './useAudioRecorder';

export interface AudioPlayerProps {
  src: string;
  durationSeconds?: number;
  compact?: boolean;
  onDelete?: () => void;
  className?: string;
  theme?: 'sent' | 'received' | 'preview';
}

const SPEED_OPTIONS = [1.0, 1.5, 2.0];

// Generates stable pseudo-waveform heights based on a seed or index
const WAVEFORM_BAR_COUNT = 28;
function generateWaveformBars(barCount: number, seed?: string): number[] {
  const bars: number[] = [];
  let num = 42;
  if (seed) {
    for (let i = 0; i < seed.length; i++) {
      num = (num * 31 + seed.charCodeAt(i)) % 1000;
    }
  }
  for (let i = 0; i < barCount; i++) {
    // Generate height between 20% and 95%
    const pseudo = ((Math.sin(i * 0.7 + num) + 1) / 2) * 60 + 20;
    bars.push(Math.round(pseudo));
  }
  return bars;
}

export const AudioPlayer: FC<AudioPlayerProps> = ({
  src,
  durationSeconds,
  compact = false,
  className = '',
  theme = 'preview',
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(durationSeconds || 0);
  const [playbackRateIndex, setPlaybackRateIndex] = useState(0);
  const [isUnavailable, setIsUnavailable] = useState(!src || !src.trim());

  const waveformBars = useMemo(() => generateWaveformBars(WAVEFORM_BAR_COUNT, src), [src]);

  // Sync initial duration & src
  useEffect(() => {
    if (durationSeconds && durationSeconds > 0) {
      setTotalDuration(durationSeconds);
    }
    if (!src || !src.trim()) {
      setIsUnavailable(true);
    } else {
      setIsUnavailable(false);
    }
  }, [durationSeconds, src]);

  // Handle Play/Pause gracefully
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || isUnavailable) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          // Gracefully mark unavailable without throwing or polluting console
          setIsUnavailable(true);
          setIsPlaying(false);
        });
    }
  }, [isPlaying, isUnavailable]);

  // Handle Speed Change
  const cyclePlaybackRate = useCallback(() => {
    const nextIndex = (playbackRateIndex + 1) % SPEED_OPTIONS.length;
    setPlaybackRateIndex(nextIndex);
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEED_OPTIONS[nextIndex];
    }
  }, [playbackRateIndex]);

  // Handle Seek
  const safeDuration =
    totalDuration && isFinite(totalDuration) && totalDuration > 0
      ? totalDuration
      : durationSeconds && isFinite(durationSeconds) && durationSeconds > 0
        ? durationSeconds
        : 0;

  const handleScrubberClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !safeDuration || isUnavailable) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percentage * safeDuration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const progressPercentage = safeDuration > 0 ? (currentTime / safeDuration) * 100 : 0;

  if (isUnavailable) {
    return (
      <div
        className={`audio-player-component audio-unavailable theme-${theme} ${compact ? 'compact' : ''} ${className}`}
      >
        <div className="audio-unavailable-badge">
          <AudioMutedOutlined className="unavailable-icon" />
          <span className="unavailable-text">Voice note unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`audio-player-component theme-${theme} ${compact ? 'compact' : ''} ${className}`}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onError={() => {
          // Media deleted or source unsupported
          setIsUnavailable(true);
          setIsPlaying(false);
        }}
        onLoadedMetadata={() => {
          const d = audioRef.current?.duration;
          if (d && !isNaN(d) && isFinite(d) && d > 0) {
            setTotalDuration(d);
          } else if (durationSeconds && isFinite(durationSeconds) && durationSeconds > 0) {
            setTotalDuration(durationSeconds);
          }
        }}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
          }
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
          if (audioRef.current) {
            audioRef.current.currentTime = 0;
          }
        }}
      />

      {/* Play/Pause Button */}
      <button
        type="button"
        className={`audio-play-btn ${isPlaying ? 'playing' : ''}`}
        onClick={togglePlayPause}
        aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
      >
        {isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>

      {/* Interactive Waveform Scrubber */}
      <div
        className="audio-waveform-container"
        onClick={handleScrubberClick}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={safeDuration}
        aria-valuenow={currentTime}
        tabIndex={0}
      >
        <div className="waveform-bars">
          {waveformBars.map((heightPercent, idx) => {
            const barProgress = (idx / WAVEFORM_BAR_COUNT) * 100;
            const isPlayed = barProgress <= progressPercentage;
            return (
              <span
                key={idx}
                className={`waveform-bar ${isPlayed ? 'played' : ''}`}
                style={{ height: `${heightPercent}%` }}
              />
            );
          })}
        </div>
      </div>

      {/* Time Display */}
      <div className="audio-time-label">
        {isPlaying || currentTime > 0
          ? formatAudioDuration(currentTime)
          : formatAudioDuration(safeDuration)}
      </div>

      {/* Playback Speed Pill */}
      <button
        type="button"
        className="audio-speed-btn"
        onClick={cyclePlaybackRate}
        title="Change playback speed"
      >
        {SPEED_OPTIONS[playbackRateIndex]}x
      </button>
    </div>
  );
};
